<template>
    <div>
        <input v-for="(item, i) in creditsUsed" :key="'credit-' + i" type="hidden" name="credits-used[]" :value="JSON.stringify(item)" />
        <label class="ckp-label" for="credit_code">Numéro d'avoir</label>
        <div class="ckp-code-row">
            <input
                type="text"
                id="credit_code"
                ref="creditCode"
                class="ckp-input"
                placeholder="AV-0000000000"
                autocomplete="off"
                @keydown.enter.prevent="handleCreditSearch"
            />
            <button type="button" class="ck-button" @click="handleCreditSearch">Appliquer</button>
        </div>
        <p v-if="invalidCredit && error" class="ckp-note is-error">{{ error }}</p>

        <section v-if="creditsUsed.length" class="ckp-group ckp-applied">
            <div class="ckp-section-head">
                <h3 class="ckp-title">Avoirs appliqués</h3>
                <span class="ckp-hint">{{ creditsUsed.length }} avoir{{ creditsUsed.length > 1 ? 's' : '' }}</span>
            </div>
            <div v-for="(item, i) in creditsUsed" :key="item.id || i" class="ckp-card ckp-applied-card">
                <div class="ckp-applied-main">
                    <strong>{{ item.serial_number }}</strong>
                    <small>
                        {{ euro(item.amount) }} · émis le {{ parseDate(item.issued_at) }}<template v-if="item.order_id"> sur la commande n° {{ item.order_id }}</template>
                        <template v-if="toAmount(item.remaining_amount) < toAmount(item.amount)"> · reste {{ euro(item.remaining_amount) }}</template>
                    </small>
                </div>
                <label class="ckp-impute">
                    <span>À imputer</span>
                    <span class="ckp-money">
                        <i>€</i>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0"
                            :max="creditMaxAmount(item)"
                            v-model="item.used_amount"
                            @input="clampCreditAmount(item)"
                            @blur="clampCreditAmount(item)"
                            :aria-label="'Montant à imputer sur l\'avoir ' + item.serial_number"
                        />
                    </span>
                </label>
                <button type="button" class="ckp-icon" title="Retirer cet avoir" aria-label="Retirer cet avoir" @click="deleteCredit(item)"><i class="bi bi-x-lg"></i></button>
            </div>
        </section>
    </div>
</template>


<script>
import ApiManager from '../../Calendar/_core/ApiManager'
import { get, sync } from 'vuex-pathify'

export default {

    data: () => ({
        creditArray: [],
        creditsUsed: [],
        invalidCredit: false,
        error: null,
    }),

    computed: {
        ...sync('overlay@data', {
            c_creditUsed: 'creditUsed',
            c_payments: 'payments',
        }),
        ...get('overlay@data', {
            c_remainPrice: 'remainPrice',
            c_remainAfterGlobalPromoCode: 'remainAfterGlobalPromoCode',
        }),
    },

    watch: {

        creditArray(newVal) {
            newVal.forEach(el => {
            if (!this.creditsUsed.find(c => c.id == el.id)) {
                this.creditsUsed.push({
                id: el.id,
                serial_number: el.serial_number,
                amount: el.amount,
                remaining_amount: el.remaining_amount,
                issued_at: el.issued_at,
                order_id: el.order_id,
                used_amount: this.clampAmount(
                    Math.min(this.toAmount(this.c_remainPrice), this.toAmount(el.remaining_amount)),
                    this.toAmount(el.remaining_amount)
                )
                })
            }
            })
        },

        creditsUsed: {
            handler(newVal) {

            newVal.forEach(item => this.clampCreditAmount(item))

            this.c_creditUsed = this.creditsUsed

            this.$emit(
                'credits-update',
                newVal.map(x => this.toAmount(x.used_amount))
            )
            },
            deep: true,
        },
    },

    methods: {
        toAmount(value) {
            const amount = parseFloat(String(value ?? 0).replace(',', '.'))

            return Number.isFinite(amount) ? amount : 0
        },
        creditMaxAmount(item) {
            const currentCreditCapacity = Math.max(
                0,
                this.toAmount(this.c_remainPrice) + this.toAmount(item.used_amount)
            )
            const capacityAfterOtherPayments = Math.max(
                0,
                this.toAmount(this.c_remainAfterGlobalPromoCode)
                    - this.nonCreditPaymentsAmount()
                    - this.otherCreditsAmount(item)
            )
            const paymentCapacity = Math.min(
                currentCreditCapacity || capacityAfterOtherPayments,
                capacityAfterOtherPayments
            )

            return Math.max(
                0,
                Math.min(
                    paymentCapacity,
                    this.toAmount(item.remaining_amount)
                )
            )
        },
        nonCreditPaymentsAmount() {
            if (!this.c_payments) {
                return 0
            }

            return this.c_payments
                .filter(payment => payment.name !== 'Rbt Avoir')
                .reduce((total, payment) => total + this.toAmount(payment.value), 0)
        },
        otherCreditsAmount(currentItem) {
            return this.creditsUsed
                .filter(item => item.id !== currentItem.id)
                .reduce((total, item) => total + this.toAmount(item.used_amount), 0)
        },
        clampAmount(value, max) {
            const amount = this.toAmount(value)
            const capped = Math.min(Math.max(amount, 0), Math.max(0, this.toAmount(max)))

            return Math.round(capped * 100) / 100
        },
        clampCreditAmount(item) {
            if (item.used_amount === '' || item.used_amount === null || item.used_amount === undefined) {
                return
            }

            const capped = this.clampAmount(item.used_amount, this.creditMaxAmount(item))

            if (this.toAmount(item.used_amount) !== capped) {
                item.used_amount = capped
            }
        },

        handleCreditSearch() {

            if (!this.$refs.creditCode.value.trim()) return
            axios.get('/back-office/avoirs/check-number', {
                params: { serial_number: this.$refs.creditCode.value }
            })
            .then(res => {

                if (!res.data || typeof res.data === 'string') {
                    this.invalidCredit = true
                    this.error = res.data || "Avoir introuvable"
                    return
                }

                if (!this.creditArray.find(c => c.id == res.data.id)) {
                    this.creditArray.push(res.data)
                }

                this.invalidCredit = false
                this.$refs.creditCode.value = ''
            })
            .catch(err => {
                this.invalidCredit = true
                this.error = err.response?.data?.message || "Erreur serveur"
            })
        },

        deleteCredit(credit) {
            this.creditArray = this.creditArray.filter(c => c.id !== credit.id)
            this.creditsUsed = this.creditsUsed.filter(c => c.id !== credit.id)
        },

        euro(value) {
            return this.toAmount(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
        },
        parseDate(date) {
            return moment(date).format('DD/MM/YYYY')
        }
    }
}

</script>

<style></style>
